/**
 * OPS-11 UX Hardening: Skeleton loading placeholders
 *
 * Displayed during initial Control Tower data fetch.
 * Matches the real layout structure to prevent layout shift.
 *
 * Style: bg-slate-800 animate-pulse rounded-lg
 */

export function PulseGaugeSkeleton() {
  return (
    <div className="min-h-[320px] rounded-2xl bg-slate-800 animate-pulse p-6 flex flex-col items-center justify-center">
      <div className="h-3 w-20 bg-slate-700 rounded mb-6" />
      <div className="w-36 h-36 rounded-full bg-slate-700/60 border-4 border-slate-700 mb-4" />
      <div className="h-5 w-16 bg-slate-700 rounded-full mb-4" />
      <div className="w-full grid grid-cols-3 gap-1.5">
        <div className="h-12 bg-slate-700/40 rounded-lg" />
        <div className="h-12 bg-slate-700/40 rounded-lg" />
        <div className="h-12 bg-slate-700/40 rounded-lg" />
      </div>
    </div>
  );
}

export function OperationalSummarySkeleton() {
  return (
    <div className="min-h-[320px] rounded-2xl bg-slate-800 animate-pulse p-5">
      <div className="grid grid-cols-3 gap-3 mb-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-16 bg-slate-700/40 rounded-xl" />
        ))}
      </div>
      <div className="h-3 w-28 bg-slate-700 rounded mb-3" />
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-1">
            <div className="h-2.5 w-20 bg-slate-700/40 rounded" />
            <div className="h-1.5 bg-slate-700/40 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function PressureSkeleton() {
  return (
    <div className="min-h-[160px] rounded-2xl bg-slate-800 animate-pulse p-5">
      <div className="h-3 w-32 bg-slate-700 rounded mb-1" />
      <div className="h-2 w-40 bg-slate-700/40 rounded mb-4" />
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i}>
            <div className="h-3 w-28 bg-slate-700/40 rounded mb-1.5" />
            <div className="h-2 bg-slate-700/40 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function NeedsAttentionSkeleton() {
  return (
    <div className="min-h-[240px] rounded-2xl bg-slate-800 animate-pulse p-4">
      <div className="h-4 w-32 bg-slate-700 rounded mb-4" />
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-12 bg-slate-700/40 rounded-lg" />
        ))}
      </div>
    </div>
  );
}

export function ActionQueueSkeleton() {
  return (
    <div className="rounded-2xl bg-slate-800 animate-pulse p-4">
      <div className="h-4 w-36 bg-slate-700 rounded mb-4" />
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-14 bg-slate-700/40 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

export function GuestIntelligenceSkeleton() {
  return (
    <div className="min-h-[160px] rounded-2xl bg-slate-800 animate-pulse p-4">
      <div className="flex items-center gap-4 mb-3">
        <div className="w-12 h-10 rounded-lg bg-slate-700/60" />
        <div>
          <div className="h-3.5 w-28 bg-slate-700 rounded mb-1.5" />
          <div className="h-2.5 w-20 bg-slate-700/40 rounded" />
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="h-8 bg-slate-700/40 rounded-lg" />
        <div className="h-8 bg-slate-700/40 rounded-lg" />
      </div>
    </div>
  );
}

export function AlertsSkeleton() {
  return (
    <div className="min-h-[260px] rounded-2xl bg-slate-800 animate-pulse p-5">
      <div className="h-3 w-16 bg-slate-700 rounded mb-4" />
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 bg-slate-700/40 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

export function ShiftBriefingSkeleton() {
  return (
    <div className="min-h-[260px] rounded-2xl bg-slate-800 animate-pulse p-5">
      <div className="h-3 w-28 bg-slate-700 rounded mb-4" />
      <div className="h-2.5 w-20 bg-slate-700/40 rounded mb-2" />
      <div className="space-y-1.5 mb-4">
        <div className="h-8 bg-slate-700/40 rounded-lg" />
        <div className="h-8 bg-slate-700/40 rounded-lg" />
      </div>
      <div className="h-2.5 w-32 bg-slate-700/40 rounded mb-2" />
      <div className="space-y-2">
        <div className="h-10 bg-slate-700/40 rounded-xl" />
        <div className="h-10 bg-slate-700/40 rounded-xl" />
      </div>
    </div>
  );
}

export function TimelineSkeleton() {
  return (
    <div className="rounded-2xl bg-slate-800 animate-pulse p-5">
      <div className="h-3 w-36 bg-slate-700 rounded mb-4" />
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex gap-3 pl-4">
            <div className="w-3 h-3 rounded-full bg-slate-700/60 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <div className="h-2.5 w-12 bg-slate-700/40 rounded mb-1" />
              <div className="h-10 bg-slate-700/40 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
